import { useState, useCallback, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { messagingApi } from '@/api/messaging';

export interface SearchResult {
  message_id: string;
  content: string;
  room_name: string;
  created_at: string;
  sender_id: string;
  sender_role: 'client' | 'filmmaker';
  sender_name?: string;
  sender_avatar?: string;
  conversation_name?: string;
  conversation_avatar?: string;
}

interface UseMessageSearchOptions {
  entityId: string;
  entityRole: 'client' | 'filmmaker';
  roomName?: string; // For in-chat search
  debounceMs?: number;
  enabled?: boolean;
}

export function useMessageSearch({
  entityId,
  entityRole,
  roomName,
  debounceMs = 200,
  enabled = true,
}: UseMessageSearchOptions) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const debounceTimer = useRef<NodeJS.Timeout>();

  // Debounce the search query for fast typing
  useEffect(() => {
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      setDebouncedQuery(query);
    }, debounceMs);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [query, debounceMs]);

  // Use React Query for caching and deduplication
  const {
    data,
    isLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey: ['message-search', entityId, entityRole, roomName, debouncedQuery],
    queryFn: async () => {
      if (!debouncedQuery || debouncedQuery.length < 1) {
        return { results: [], total_count: 0 };
      }
      return messagingApi.searchMessages({
        query: debouncedQuery,
        entityId,
        entityRole,
        roomName,
      });
    },
    enabled: enabled && !!entityId && !!entityRole && debouncedQuery.length >= 1,
    staleTime: 30 * 1000, // Cache for 30 seconds
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });

  const clearSearch = useCallback(() => {
    setQuery('');
    setDebouncedQuery('');
  }, []);

  return {
    query,
    setQuery,
    results: data?.results || [],
    totalCount: data?.total_count || 0,
    isLoading: isLoading && debouncedQuery.length >= 1,
    isSearching: isFetching && debouncedQuery.length >= 1,
    error,
    clearSearch,
    hasResults: (data?.results?.length || 0) > 0,
    isActive: query.length > 0,
  };
}
